import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

const UnsubscribePage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [contactInfo, setContactInfo] = useState<any>(null);
  const [reason, setReason] = useState('');
  const [showReasonForm, setShowReasonForm] = useState(false);

  useEffect(() => {
    if (token) {
      handleUnsubscribe();
    }
  }, [token]);

  const handleUnsubscribe = async (customReason?: string) => {
    setLoading(true);
    setError('');
    
    try {
      const url = customReason
        ? `/api/marketing/unsubscribe/${token}?reason=${encodeURIComponent(customReason)}`
        : `/api/marketing/unsubscribe/${token}`;
      
      const response = await api.get(url);
      setSuccess(true);
      setContactInfo(response.data.contact);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to unsubscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReasonSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim()) {
      handleUnsubscribe(reason);
      setShowReasonForm(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Processing your request...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5 mb-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card shadow">
            <div className="card-body p-5">
              {error ? (
                <>
                  <div className="text-center mb-4">
                    <i className="bi bi-x-circle text-danger" style={{ fontSize: '4rem' }}></i>
                    <h2 className="mt-3">Unsubscribe Failed</h2>
                  </div>
                  <div className="alert alert-danger">
                    {error}
                  </div>
                  <div className="text-center mt-4">
                    <button className="btn btn-secondary" onClick={() => navigate('/')}>
                      Return to Home
                    </button>
                  </div>
                </>
              ) : success ? (
                <>
                  <div className="text-center mb-4">
                    <i className="bi bi-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                    <h2 className="mt-3">Successfully Unsubscribed</h2>
                  </div>
                  
                  {contactInfo && (
                    <div className="alert alert-success">
                      <p className="mb-0">
                        <strong>{contactInfo.name}</strong> ({contactInfo.email}) has been removed from our mailing list.
                      </p>
                      {contactInfo.unsubscribedDate && (
                        <p className="mb-0 mt-2">
                          <small>Previously unsubscribed on: {new Date(contactInfo.unsubscribedDate).toLocaleDateString()}</small>
                        </p>
                      )}
                    </div>
                  )}

                  <div className="bg-light p-4 rounded mt-4">
                    <h5>What happens next?</h5>
                    <ul className="mb-0">
                      <li>You will no longer receive marketing emails from CEC Tracker</li>
                      <li>Changes take effect immediately</li>
                      <li>You may still receive important account-related notifications if you have an active account</li>
                      <li>You can resubscribe at any time by contacting us</li>
                    </ul>
                  </div>

                  {!showReasonForm && !contactInfo?.unsubscribedDate && (
                    <div className="text-center mt-4">
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => setShowReasonForm(true)}
                      >
                        Tell us why (optional)
                      </button>
                    </div>
                  )}

                  {showReasonForm && (
                    <div className="mt-4">
                      <form onSubmit={handleReasonSubmit}>
                        <div className="mb-3">
                          <label className="form-label">Why are you unsubscribing? (Optional)</label>
                          <select 
                            className="form-select mb-2"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                          >
                            <option value="">-- Select a reason --</option>
                            <option value="Too many emails">Too many emails</option>
                            <option value="Not relevant to me">Not relevant to me</option>
                            <option value="I didn't sign up for this">I didn't sign up for this</option>
                            <option value="Privacy concerns">Privacy concerns</option>
                            <option value="Other">Other</option>
                          </select>
                          <textarea
                            className="form-control"
                            rows={3}
                            placeholder="Additional comments..."
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                          />
                        </div>
                        <div className="d-flex gap-2">
                          <button type="submit" className="btn btn-primary" disabled={!reason.trim()}>
                            Submit Feedback
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-secondary"
                            onClick={() => setShowReasonForm(false)}
                          >
                            Skip
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  <div className="text-center mt-4">
                    <button className="btn btn-secondary" onClick={() => navigate('/')}>
                      Return to Home
                    </button>
                  </div>
                </>
              ) : null}

              <div className="text-center mt-5 pt-4 border-top">
                <p className="text-muted small mb-1">
                  Changed your mind? Contact us at <a href="mailto:support@cectrack.com">support@cectrack.com</a>
                </p>
                <p className="text-muted small mb-0">
                  CEC Track — Continuing Education Compliance Made Simple
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
